import { useEffect, useRef, useState } from "react";
import { CARDS_BY_ID } from "@grundbesitz/shared";
import type { DeckId } from "@grundbesitz/shared";
import { nameOf } from "../../lib/gameView";
import { useStore } from "../../lib/store";

interface Shown {
  key: number;
  deck: DeckId;
  text: string;
  who: string;
  self: boolean;
}

const SHOW_MS = 3800;

const DECK_TITLE: Record<DeckId, string> = { chance: "Ereigniskarte", community: "Gemeinschaftskarte" };

/** Shows a drawn card to everyone for a few seconds. Mount inside a positioned container. */
export function CardOverlay() {
  const subscribe = useStore((s) => s.subscribeEvents);
  const [shown, setShown] = useState<Shown | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const counter = useRef(0);

  useEffect(() => {
    const off = subscribe((batch) => {
      const drawn = batch.events.find((e) => e.type === "CARD_DRAWN");
      if (!drawn || drawn.type !== "CARD_DRAWN") return;
      const card = CARDS_BY_ID[drawn.cardId];
      const { room } = useStore.getState();
      if (!card || !room) return;
      setShown({
        key: ++counter.current,
        deck: drawn.deck,
        text: card.text,
        who: nameOf(room.game, room.members, drawn.playerId),
        self: drawn.playerId === room.you.playerId,
      });
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setShown(null), SHOW_MS);
    });
    return () => {
      off();
      if (timer.current) clearTimeout(timer.current);
    };
  }, [subscribe]);

  if (!shown) return null;
  const chance = shown.deck === "chance";
  return (
    <div className="pointer-events-none absolute inset-0 z-20 grid place-items-center p-4" role="status" aria-live="polite">
      <div
        key={shown.key}
        className={`w-full max-w-xs rounded-2xl border-2 bg-surface p-4 text-center shadow-2xl ${chance ? "border-accent" : "border-warn"}`}
      >
        <p className="text-sm font-semibold text-ink-2">
          {shown.self ? "Du ziehst" : `${shown.who} zieht`}: {DECK_TITLE[shown.deck]}
        </p>
        <p className="mt-2 font-display text-lg font-semibold leading-snug text-ink">{shown.text}</p>
      </div>
    </div>
  );
}
